import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import ErrorLogPanel from './ErrorLogPanel';
import { APP_VERSION } from '../config/version';
import { 
  LayoutDashboard, 
  Calendar, 
  Clock, 
  Users, 
  BarChart3,
  LogOut,
  Menu,
  X,
  Moon,
  Sun,
  ClipboardCheck,
  CalendarDays,
  Settings,
  User,
  Send,
  Globe,
  HelpCircle
} from 'lucide-react';

const VITALIS_LOGO = "https://vitalis-sports.ch/wp-content/uploads/2024/12/logo_web.png";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = {
    collaborateur: [
      { path: '/dashboard', label: t('dashboard'), icon: LayoutDashboard },
      { path: '/schedule', label: t('schedule'), icon: Calendar },
      { path: '/absences', label: t('absences'), icon: Clock },
      { path: '/reporting', label: t('reportingPlanning'), icon: ClipboardCheck },
    ],
    manager: [
      { path: '/dashboard', label: t('dashboard'), icon: LayoutDashboard },
      { path: '/schedule', label: t('schedule'), icon: Calendar },
      { path: '/absences', label: t('absences'), icon: Clock },
      { path: '/reporting', label: t('reportingPlanning'), icon: ClipboardCheck },
      { path: '/leave', label: t('leaveBalance'), icon: CalendarDays },
      { path: '/communicate', label: t('teamCommunication'), icon: Send },
      { path: '/team', label: t('team'), icon: Users },
      { path: '/stats', label: t('stats'), icon: BarChart3 },
    ],
    directeur: [
      { path: '/dashboard', label: t('dashboard'), icon: LayoutDashboard },
      { path: '/schedule', label: t('schedule'), icon: Calendar },
      { path: '/absences', label: t('absences'), icon: Clock },
      { path: '/reporting', label: t('reportingPlanning'), icon: ClipboardCheck },
      { path: '/leave', label: t('leaveBalance'), icon: CalendarDays },
      { path: '/communicate', label: t('teamCommunication'), icon: Send },
      { path: '/users', label: t('users'), icon: Users },
      { path: '/stats', label: t('stats'), icon: BarChart3 },
      { path: '/admin', label: t('administration'), icon: Settings },
    ],
  };

  const items = navItems[user?.role] || navItems.collaborateur;

  return (
    <div className="flex min-h-screen">
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-14 border-b border-border bg-background z-50 flex items-center justify-between px-4">
        <img src={VITALIS_LOGO} alt="Vitalis Sports" className="h-8" />
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2"
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 bottom-0 w-60 bg-card border-r border-border z-50 transform transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}>
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="h-16 flex items-center justify-center border-b border-border bg-white dark:bg-slate-800">
            <img src={VITALIS_LOGO} alt="Vitalis Sports" className="h-10" />
          </div>

          {/* Language Selector */}
          <div className="flex justify-center gap-1 p-3 border-b border-border">
            {['fr', 'en', 'de'].map((lang) => (
              <button
                key={lang}
                onClick={() => setLanguage(lang)}
                className={`lang-btn ${language === lang ? 'active' : ''}`}
                data-testid={`lang-${lang}`}
              >
                {lang.toUpperCase()}
              </button>
            ))}
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4 px-2 overflow-y-auto">
            {items.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.path.replace('/', '')}`}
                  onClick={() => setSidebarOpen(false)}
                  className={`sidebar-link mb-1 ${isActive ? 'active' : ''}`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
            
            {/* Help link for managers/directors */}
            {(user?.role === 'manager' || user?.role === 'directeur') && (
              <>
                <div className="my-3 border-t border-border" />
                <Link
                  to="/help"
                  data-testid="nav-help"
                  onClick={() => setSidebarOpen(false)}
                  className={`sidebar-link mb-1 ${location.pathname === '/help' ? 'active' : ''}`}
                >
                  <HelpCircle className="h-4 w-4" />
                  <span>{t('help')}</span>
                </Link>
              </>
            )}
          </nav>

          {/* User Info & Actions */}
          <div className="p-4 border-t border-border space-y-3">
            <div className="flex items-center justify-between">
              <Link 
                to="/profile" 
                className="text-sm hover:text-primary transition-colors"
                onClick={() => setSidebarOpen(false)}
                data-testid="nav-profile"
              >
                <p className="font-medium">{user?.first_name} {user?.last_name}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
              </Link>
              <div className="flex items-center gap-1">
                <Link
                  to="/profile"
                  onClick={() => setSidebarOpen(false)}
                  className="p-2 rounded-md border border-border hover:border-primary transition-colors"
                  title={t('myProfile')}
                >
                  <User className="h-4 w-4" />
                </Link>
                <button
                  onClick={toggleDarkMode}
                  className="p-2 rounded-md border border-border hover:border-primary transition-colors"
                  data-testid="dark-mode-toggle"
                >
                  {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-btn"
              className="w-full"
            >
              <LogOut className="h-3 w-3 mr-2" />
              {t('logout')}
            </Button>
            
            {/* Error Log & Version */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
              <ErrorLogPanel />
              <span className="text-xs text-muted-foreground">v{APP_VERSION}</span>
            </div>
            
            {/* Public site link */}
            <Link
              to="/site"
              className="flex items-center justify-center gap-2 mt-3 py-2 px-3 text-xs text-muted-foreground hover:text-primary bg-muted/30 hover:bg-muted rounded-md transition-colors"
              data-testid="public-site-link"
            >
              <Globe className="h-3 w-3" />
              {t('publicSite')}
            </Link>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 md:ml-60">
        <div className="md:hidden h-14" />
        <div className="p-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
}
